import ContentIllustration from '../../../components/ui/ContentIllustration';
import ScopeIllustration from '../../../components/ui/ScopeIllustration';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Check, GripVertical, ChevronUp, ChevronDown, ChevronRight, Search, ArrowRight, ArrowLeft, Users, Share2, Sparkles, FolderTree, Tag } from 'lucide-react';
import DynamicIcon from '../../../components/ui/DynamicIcon';
import RedeemCodeInput from '../../../components/share/RedeemCodeInput';

// Onglet Thématiques du compte : sélection des scopes affichés + écran d'ordre d'affichage.
export default function CompteThematiques({
    scopes, categories, activeScopeIds,
    prefs, setSelectedScopes, setLastScope, setScopeOrder, updatePrefs,
    sharedThematiques, onRedeemed,
}) {
    const navigate = useNavigate();
    const [themeScreen, setThemeScreen] = useState('selection'); // 'selection' | 'order'
    const [dragIdx, setDragIdx] = useState(null);
    const [partnerSearch, setPartnerSearch] = useState('');
    const [expandedCats, setExpandedCats] = useState(new Set());

    const selectedScopes = prefs.selectedScopes || [];
    const hasSelectedScopes = selectedScopes.length > 0;

    const toggleScope = (slug) => {
        const next = selectedScopes.includes(slug)
            ? selectedScopes.filter(s => s !== slug)
            : [...selectedScopes, slug];
        setSelectedScopes(next);
    };

    const sortScopes = (list) => {
        const order = prefs.scopeOrder || [];
        if (!order.length) return list;
        return [...list].sort((a, b) => {
            const ia = order.indexOf(a.slug);
            const ib = order.indexOf(b.slug);
            if (ia === -1 && ib === -1) return 0;
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });
    };

    const sortedScopes = sortScopes(scopes);
    // All orderable scopes: selected editorial/partner + active shared
    const sharedScopeItems = (sharedThematiques || []).filter(st => st.has_access).map(st => ({
        ...st, slug: st.slug, label: st.label.replace(/^Ma Thematique — /, ''), is_shared_item: true,
        image: st.image || scopes.find(s => s.slug === 'mes-thematiques')?.image || null,
    }));
    const selectedSorted = sortScopes([
        ...scopes.filter(s => selectedScopes.includes(s.slug) && s.slug !== 'mes-thematiques' && (s.content_source || 'editorial') !== 'user'),
        ...sharedScopeItems,
    ]);

    const moveScope = (idx, dir) => {
        const slugs = selectedSorted.map(s => s.slug);
        const targetIdx = idx + dir;
        if (targetIdx < 0 || targetIdx >= slugs.length) return;
        [slugs[idx], slugs[targetIdx]] = [slugs[targetIdx], slugs[idx]];
        const unselected = sortedScopes.filter(s => !selectedScopes.includes(s.slug)).map(s => s.slug);
        setScopeOrder([...slugs, ...unselected]);
    };

    const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
    const handleDragOver = (e, idx) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        const slugs = selectedSorted.map(s => s.slug);
        const [moved] = slugs.splice(dragIdx, 1);
        slugs.splice(idx, 0, moved);
        const unselected = sortedScopes.filter(s => !selectedScopes.includes(s.slug)).map(s => s.slug);
        setScopeOrder([...slugs, ...unselected]);
        setDragIdx(idx);
    };
    const handleDragEnd = () => setDragIdx(null);

    const editorialScopes = sortedScopes.filter(s =>
        s.slug !== 'thematique-personnalisable' && s.slug !== 'mes-thematiques'
        && (s.content_source || 'editorial') === 'editorial'
    );
    const partnerScopes = sortedScopes.filter(s => (s.content_source || 'editorial') === 'partner');
    const filteredPartners = partnerScopes.filter(s =>
        !partnerSearch || s.label.toLowerCase().includes(partnerSearch.toLowerCase())
        || (s.partner_name || '').toLowerCase().includes(partnerSearch.toLowerCase())
        || (s.description || '').toLowerCase().includes(partnerSearch.toLowerCase())
    );

    // === SCREEN 2: ORDER ===
    if (themeScreen === 'order') {
        return (
            <div className="scope-selector">
                <div className="scope-selector-header">
                    <button className="btn btn-sm btn-ghost" onClick={() => setThemeScreen('selection')}>
                        <ArrowLeft size={16} /> Retour à la sélection
                    </button>
                    <h2>Ordre d'affichage</h2>
                    <p>Glissez ou utilisez les flèches pour réorganiser vos thématiques.</p>
                </div>

                <div className="scope-order-list">
                    {selectedSorted.map((scope, idx) => {
                        const isPartner = (scope.content_source || 'editorial') === 'partner';
                        const isShared = !!scope.is_shared_item;
                        // Rendu compact (icone + petite ligne) uniquement quand il n'y a pas
                        // de visuel : une publication partenaire avec image s'affiche comme
                        // une thematique editoriale (fond + vignette).
                        const isCompact = isPartner && !scope.image;
                        return (
                            <div
                                key={scope.slug}
                                className={`scope-order-item ${isCompact ? 'scope-order-item--compact' : ''} ${scope.image ? 'scope-order-item--bg' : ''} ${dragIdx === idx ? 'scope-selector-dragging' : ''}`}
                                draggable onDragStart={(e) => handleDragStart(e, idx)}
                                onDragOver={(e) => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
                                style={!isCompact && scope.image ? { backgroundImage: `url(${scope.image})` } : undefined}
                            >
                                <span className="scope-order-rank">{idx + 1}</span>
                                {!isCompact && scope.image
                                    ? <img src={scope.image} alt="" className="scope-order-img" />
                                    : scope.icon ? <ScopeIllustration icon={scope.icon} className="content-scope-illu--sm" />
                                    : isShared ? <ContentIllustration name="share-2" size={24} />
                                    : isPartner ? <ContentIllustration name="users" size={24} />
                                    : <ScopeIllustration className="content-scope-illu--sm" />}
                                <span className="scope-order-label">
                                    {scope.label}
                                    {isPartner && scope.partner_name && (
                                        <span className="scope-order-partner-badge">par {scope.partner_name}</span>
                                    )}
                                    {isShared && scope.owner_name && (
                                        <span className="scope-order-partner-badge">partagé par {scope.owner_name}</span>
                                    )}
                                </span>
                                <div className="scope-order-arrows">
                                    <button className="scope-selector-move" onClick={() => moveScope(idx, -1)} disabled={idx === 0}><ChevronUp size={14} /></button>
                                    <button className="scope-selector-move" onClick={() => moveScope(idx, 1)} disabled={idx === selectedSorted.length - 1}><ChevronDown size={14} /></button>
                                </div>
                                <GripVertical size={14} className="scope-order-grip" />
                            </div>
                        );
                    })}
                </div>

                <div className="scope-selector-actions">
                    <button className="btn btn-primary" onClick={() => {
                        const target = prefs.lastScope && selectedScopes.includes(prefs.lastScope) ? prefs.lastScope : selectedScopes[0];
                        setLastScope(target);
                        navigate(`/compte/quiz/${target}`, { replace: true });
                    }}>
                        Accéder à mes thématiques
                    </button>
                </div>
            </div>
        );
    }

    // === SCREEN 1: SELECTION ===
    // Carte du hub « Mes Thématiques » (création de QCM depuis ses propres
    // documents). Sa place dépend du moment : voir plus bas.
    const hubCard = (() => {
        const hubScope = scopes.find(s => s.slug === 'mes-thematiques');
        if (!hubScope) return null;
        return (
            <div style={{ marginBottom: '16px', maxWidth: '560px', margin: '0 auto 16px' }}>
                <div
                    className={`scope-selector-card scope-selector-selected scope-selector-card--locked ${hubScope.image ? 'scope-selector-card--bg' : ''}`}
                    style={{
                        ...(hubScope.couleur_theme ? { '--scope-color': hubScope.couleur_theme } : {}),
                        ...(hubScope.image ? { backgroundImage: `url(${hubScope.image})` } : {}),
                        cursor: 'default', opacity: 0.75,
                    }}
                >
                    <div className="scope-selector-check"><Check size={18} strokeWidth={2.5} /></div>
                    {!hubScope.image && (
                        <div className="scope-selector-icon"><ContentIllustration name="sparkles" size={48} /></div>
                    )}
                    <div className="scope-selector-text">
                        <h3>{hubScope.label}</h3>
                        <p style={{ fontSize: '0.8em' }}>Toujours disponible — créez vos QCM depuis vos documents</p>
                    </div>
                </div>
            </div>
        );
    })();

    return (
        <div className="scope-selector">
            <div className="scope-selector-header">
                <h2>{hasSelectedScopes ? 'Gérer mes thématiques' : 'Quel concours préparez-vous ?'}</h2>
                <p>{hasSelectedScopes
                    ? 'Activez ou désactivez les thématiques affichées sur votre accueil.'
                    : 'Choisissez votre concours pour commencer : chaque thématique propose des sujets d’essai gratuits.'
                }</p>
            </div>

            {/* Le hub « Mes Thématiques » n'est en tête que pour quelqu'un qui a
                déjà choisi ses concours. À la première visite il passe APRÈS le
                catalogue : mis en avant, il détournait les nouveaux inscrits vers
                la création de QCM depuis leurs documents alors qu'ils venaient
                chercher un concours précis. */}
            {hasSelectedScopes && hubCard}

            {/* --- Editorial scopes --- */}
            <h3 className="scope-selector-section-label">
                <ContentIllustration name="book-open" size={32} /> Thématiques éditoriales
            </h3>
            <div className="scope-selector-grid">
                {editorialScopes.map((scope) => {
                    const isSelected = selectedScopes.includes(scope.slug);
                    const isSubscribed = activeScopeIds.includes(scope.id);
                    return (
                        <div
                            key={scope.id}
                            className={`scope-selector-card ${isSelected ? 'scope-selector-selected' : ''} ${scope.image ? 'scope-selector-card--bg' : ''}`}
                            role="button" tabIndex={0}
                            onClick={() => toggleScope(scope.slug)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleScope(scope.slug); }}
                            style={{
                                ...(scope.couleur_theme ? { '--scope-color': scope.couleur_theme } : {}),
                                ...(scope.image ? { backgroundImage: `url(${scope.image})` } : {}),
                            }}
                        >
                            <div className="scope-selector-check">{isSelected && <Check size={18} strokeWidth={2.5} />}</div>
                            {!scope.image && (
                                <div className="scope-selector-icon" style={scope.couleur_theme ? { color: scope.couleur_theme } : undefined}>
                                    <ScopeIllustration icon={scope.icon} className="content-scope-illu" />
                                </div>
                            )}
                            <div className="scope-selector-text">
                                <h3>{scope.label}</h3>
                                {scope.description && <p>{scope.description}</p>}
                                <span className="scope-selector-card-meta">
                                    <span>{scope.nb_questions || 0} questions &middot; {scope.nb_sous_sujets || 0} sujets</span>
                                </span>
                                {isSubscribed && <span className="scope-selector-badge-active">Abonné</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {!hasSelectedScopes && hubCard && (
                <>
                    <h3 className="scope-selector-section-label">
                        <Sparkles size={16} /> Vos propres documents
                    </h3>
                    {hubCard}
                </>
            )}

            {/* --- Partner / community scopes --- */}
            <h3 className="scope-selector-section-label scope-selector-section-label--partner">
                <ContentIllustration name="users" size={32} /> Thématiques communautaires
            </h3>
            {partnerScopes.length === 0 ? (
                <div className="scope-selector-partner-empty">
                    <p>Aucune thématique communautaire disponible pour le moment.</p>
                    <p className="scope-selector-partner-hint">
                        Les utilisateurs peuvent créer et publier leurs propres thématiques via &laquo;&nbsp;Mes Thématiques&nbsp;&raquo;.
                    </p>
                </div>
            ) : (
                <>
                    <div className="scope-selector-partner-search">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Rechercher une thématique communautaire..."
                            value={partnerSearch}
                            onChange={(e) => setPartnerSearch(e.target.value)}
                        />
                    </div>
                    {(() => {
                        // Build category tree for partner scopes
                        const rootCats = categories.filter(c => !c.parent_id);
                        const getSubCats = (parentId) => categories.filter(c => c.parent_id == parentId);
                        // Scopes in a specific (sub)category
                        const scopesInCat = (catId) => filteredPartners.filter(s => s.category?.id === catId);
                        // Scopes in a root category (direct + all subcategories)
                        const scopesInRoot = (rootId) => {
                            const subIds = getSubCats(rootId).map(c => c.id);
                            return filteredPartners.filter(s => {
                                const sc = s.category;
                                if (!sc) return false;
                                return sc.id === rootId || subIds.includes(sc.id);
                            });
                        };
                        const uncategorized = filteredPartners.filter(s => !s.category);
                        const hasCats = rootCats.length > 0 && filteredPartners.some(s => s.category);

                        const toggleCat = (id) => setExpandedCats(prev => {
                            const next = new Set(prev);
                            next.has(id) ? next.delete(id) : next.add(id);
                            return next;
                        });

                        // Même carte que les thématiques éditoriales (image de fond, coche,
                        // titre, description), enrichie de ce qui distingue une publication :
                        // la signature de l'auteur, sa catégorie, et le volume de contenu.
                        const renderPartnerItem = (scope) => {
                            const isSelected = selectedScopes.includes(scope.slug);
                            const isSubscribed = activeScopeIds.includes(scope.id);
                            return (
                                <div
                                    key={scope.id}
                                    className={`scope-selector-card ${isSelected ? 'scope-selector-selected' : ''} ${scope.image ? 'scope-selector-card--bg' : ''}`}
                                    role="button" tabIndex={0}
                                    onClick={() => toggleScope(scope.slug)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleScope(scope.slug); }}
                                    style={{
                                        ...(scope.couleur_theme ? { '--scope-color': scope.couleur_theme } : {}),
                                        ...(scope.image ? { backgroundImage: `url(${scope.image})` } : {}),
                                    }}
                                >
                                    <div className="scope-selector-check">{isSelected && <Check size={18} strokeWidth={2.5} />}</div>
                                    {!scope.image && (
                                        <div className="scope-selector-icon" style={scope.couleur_theme ? { color: scope.couleur_theme } : undefined}>
                                            <ScopeIllustration icon={scope.icon} className="content-scope-illu" />
                                        </div>
                                    )}
                                    <div className="scope-selector-text">
                                        <h3>{scope.label}</h3>
                                        {scope.partner_name && <span className="scope-selector-card-author">par {scope.partner_name}</span>}
                                        {scope.description && <p>{scope.description}</p>}
                                        <span className="scope-selector-card-meta">
                                            {scope.category && (
                                                <span className="scope-selector-partner-cat">
                                                    {scope.category.parent ? `${scope.category.parent.name} · ` : ''}{scope.category.name}
                                                </span>
                                            )}
                                            <span>{scope.nb_questions || 0} questions · {scope.nb_sous_sujets || 0} sujets</span>
                                        </span>
                                        {isSubscribed && <span className="scope-selector-badge-active">Abonné</span>}
                                    </div>
                                </div>
                            );
                        };

                        if (!hasCats) {
                            // No categories — flat list (backward compat)
                            return (
                                <div className="scope-selector-partner-list">
                                    {filteredPartners.map(renderPartnerItem)}
                                    {filteredPartners.length === 0 && partnerSearch && (
                                        <p className="scope-selector-partner-noresult">Aucun résultat pour « {partnerSearch} »</p>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div className="scope-selector-partner-cats">
                                {rootCats.map(root => {
                                    const subCats = getSubCats(root.id);
                                    const rootScopes = scopesInRoot(root.id);
                                    const directScopes = scopesInCat(root.id);
                                    if (rootScopes.length === 0) return null;
                                    const isExpanded = expandedCats.has(root.id);
                                    return (
                                        <div key={root.id} className="scope-selector-cat-group">
                                            <button
                                                className="scope-selector-cat-header"
                                                onClick={() => toggleCat(root.id)}
                                            >
                                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                {root.icon ? <DynamicIcon name={root.icon} size={16} /> : <FolderTree size={16} />}
                                                <span className="scope-selector-cat-name">{root.name}</span>
                                                <span className="scope-selector-cat-count">{rootScopes.length}</span>
                                            </button>
                                            {isExpanded && (
                                                <div className="scope-selector-cat-body">
                                                    {/* Direct scopes under root */}
                                                    {directScopes.length > 0 && (
                                                        <div className="scope-selector-partner-list">
                                                            {directScopes.map(renderPartnerItem)}
                                                        </div>
                                                    )}
                                                    {/* Subcategories */}
                                                    {subCats.map(sub => {
                                                        const subScopes = scopesInCat(sub.id);
                                                        if (subScopes.length === 0) return null;
                                                        return (
                                                            <div key={sub.id} className="scope-selector-subcat-group">
                                                                <div className="scope-selector-subcat-header">
                                                                    {sub.icon ? <DynamicIcon name={sub.icon} size={14} /> : <Tag size={14} />}
                                                                    <span>{sub.name}</span>
                                                                    <span className="scope-selector-cat-count">{subScopes.length}</span>
                                                                </div>
                                                                <div className="scope-selector-partner-list">
                                                                    {subScopes.map(renderPartnerItem)}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {/* Uncategorized */}
                                {uncategorized.length > 0 && (
                                    <div className="scope-selector-cat-group">
                                        <button
                                            className="scope-selector-cat-header"
                                            onClick={() => toggleCat('_uncategorized')}
                                        >
                                            {expandedCats.has('_uncategorized') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            <ContentIllustration name="users" size={32} />
                                            <span className="scope-selector-cat-name">Autres</span>
                                            <span className="scope-selector-cat-count">{uncategorized.length}</span>
                                        </button>
                                        {expandedCats.has('_uncategorized') && (
                                            <div className="scope-selector-cat-body">
                                                <div className="scope-selector-partner-list">
                                                    {uncategorized.map(renderPartnerItem)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {filteredPartners.length === 0 && partnerSearch && (
                                    <p className="scope-selector-partner-noresult">Aucun résultat pour « {partnerSearch} »</p>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}

            {/* --- Shared thematiques (always visible) --- */}
            <h3 className="scope-selector-section-label" style={{ marginTop: 24 }}>
                <ContentIllustration name="share-2" size={32} /> Thématiques partagées
            </h3>
            {sharedThematiques.length > 0 && (
                <div className="scope-selector-grid" style={{ marginBottom: 12 }}>
                    {sharedThematiques.map(st => {
                        const cleanLabel = st.label.replace(/^Ma Thematique — /, '');
                        const fallbackImage = scopes.find(s => s.slug === 'mes-thematiques')?.image;
                        const bgImage = st.image || fallbackImage;
                        const hiddenShared = prefs.hiddenSharedScopes || [];
                        const isHidden = hiddenShared.includes(st.slug);
                        return (
                            <div
                                key={`shared-${st.id}`}
                                className={`scope-selector-card ${isHidden ? '' : 'scope-selector-selected'} ${bgImage ? 'scope-selector-card--bg' : ''}`}
                                style={{
                                    ...(st.couleur_theme ? { '--scope-color': st.couleur_theme } : {}),
                                    ...(bgImage ? { backgroundImage: `url(${bgImage})` } : {}),
                                    ...(isHidden ? { opacity: 0.5 } : {}),
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    const current = prefs.hiddenSharedScopes || [];
                                    const next = isHidden ? current.filter(s => s !== st.slug) : [...current, st.slug];
                                    updatePrefs({ hiddenSharedScopes: next });
                                }}
                            >
                                <div className="scope-selector-check">
                                    <Share2 size={16} strokeWidth={2.5} fill={isHidden ? 'none' : 'currentColor'} />
                                </div>
                                <div className="scope-selector-text">
                                    <h3>{cleanLabel}</h3>
                                    {st.owner_name && <p style={{ fontSize: '0.8em', opacity: 0.8 }}>Partagé par {st.owner_name}</p>}
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                        <span style={{ fontSize: '0.75em' }}>{st.nb_questions || 0} questions</span>
                                        {!!Number(st.has_access) && st.subscription_end && (
                                            <span style={{ fontSize: '0.75em' }}>jusqu'au {new Date(st.subscription_end + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
                                        )}
                                    </div>
                                    {st.has_access
                                        ? <span className="scope-selector-badge-active">{isHidden ? 'Masqué' : 'Actif'}</span>
                                        : <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>Expiré</span>
                                    }
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: 8 }}>
                Vous avez reçu un code de partage ? Saisissez-le ci-dessous pour accéder à une thématique.
            </p>
            <RedeemCodeInput onRedeemed={onRedeemed} />

            {/* --- Actions --- */}
            <div className="scope-selector-actions">
                <button className="btn btn-primary" onClick={() => {
                    if (hasSelectedScopes) {
                        const target = prefs.lastScope && selectedScopes.includes(prefs.lastScope) ? prefs.lastScope : selectedScopes[0];
                        setLastScope(target);
                        navigate(`/compte/quiz/${target}`, { replace: true });
                    } else {
                        navigate('/compte/quiz/mes-thematiques', { replace: true });
                    }
                }}>
                    Accéder à mes quiz <ArrowRight size={16} />
                </button>
                {selectedSorted.length > 1 && (
                    <button className="btn btn-secondary" onClick={() => setThemeScreen('order')}>
                        Organiser l'ordre d'affichage
                    </button>
                )}
            </div>
        </div>
    );
}
